import {none, some} from '@danwang/option';
import {
  PatchOpType,
  useAllFieldsQuery,
  usePatchTransactionsMutation,
} from '@transactions/graphql';
import * as React from 'react';
import {addNewCategory} from 'src/apolloCache/updateCategories';
import {addPayee} from 'src/apolloCache/updatePayees';
import {addNewTags} from 'src/apolloCache/updateTags';
import {Button} from 'src/Button';
import CategoryPicker from 'src/CategoryPicker';
import {
  MIXED,
  PropertyRow,
  RemovalChip,
  uniform,
} from 'src/components/BulkEditParts';
import PayeeInput from 'src/inputs/PayeeInput';
import {PendingCheckbox} from 'src/inputs/PendingCheckbox';
import {TagsAndFieldsInput} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {ApolloCache, FetchResult} from '@apollo/client';
import type {Option} from '@danwang/option';
import type {
  PatchTransactionsMutation,
  TransactionPatchOp,
  UnifiedTransactionFragment,
} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type BulkEditTransactionsSheetUserProps = {
  transactions: UnifiedTransactionFragment[];
  onSaved?: () => void;
};

type PropertyKey = 'payee' | 'category' | 'description' | 'tagsAndFields' | 'pending';

/**
 * Edits several transactions at once. Every property starts as "leave
 * unchanged" and applies only once activated, so a mixed selection is safe:
 * "set this on all of these, touch nothing else". Payee and category apply to
 * the payments in the selection; transfers carry neither.
 */
export const BulkEditTransactionsSheet = ({
  transactions,
  onSaved,
  onRequestClose,
}: BulkEditTransactionsSheetUserProps & SystemProps) => {
  const formId = React.useId();
  const {
    data: fieldsData,
    error: fieldsError,
    refetch: refetchFields,
  } = useAllFieldsQuery();
  const payments = transactions.filter((t) => t.netAmount !== 0);
  const transfers = transactions.filter((t) => t.netAmount === 0);
  const transferCount = transfers.length;
  const categoryReason =
    payments.length === 0 ? 'Transfers have no category' : null;

  const [active, setActive] = React.useState<ReadonlySet<PropertyKey>>(new Set());
  const [payee, setPayee] = React.useState('');
  const [category, setCategory] = React.useState<Option<string>>(none);
  const [description, setDescription] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [addTags, setAddTags] = React.useState<string[]>([]);
  const [setFields, setSetFields] = React.useState<Record<string, string>>({});
  const [removeTags, setRemoveTags] = React.useState<ReadonlySet<string>>(new Set());
  const [removeFieldIds, setRemoveFieldIds] = React.useState<ReadonlySet<string>>(
    new Set(),
  );
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // A payee spans payments and transfers (a transfer's names its
  // counterparty); a category is a payment's alone.
  const uniformPayee = uniform(transactions, (t) => t.payee ?? '');
  const uniformCategory = uniform(payments, (t) => t.category?.name ?? null);
  const uniformDescription = uniform(transactions, (t) => t.description ?? '');
  const uniformPending = uniform(transactions, (t) => t.pending);

  const tagCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of transactions) {
      for (const tag of t.tags) counts.set(tag.name, (counts.get(tag.name) ?? 0) + 1);
    }
    return counts;
  }, [transactions]);
  const fieldCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of transactions) {
      for (const fieldId of Object.keys(t.fieldValues ?? {})) {
        counts.set(fieldId, (counts.get(fieldId) ?? 0) + 1);
      }
    }
    return counts;
  }, [transactions]);
  const fieldName = (fieldId: string) =>
    fieldsData?.allFields.find((f) => f._id === fieldId)?.name ?? fieldId;

  const isActive = (key: PropertyKey) => active.has(key);
  const toggleRow = (key: PropertyKey) => (next: boolean) => {
    setActive((prev) => {
      const nextSet = new Set(prev);
      if (next) nextSet.add(key);
      else nextSet.delete(key);
      return nextSet;
    });
    if (!next) return;
    // Activating seeds the draft with the value the selection agrees on.
    switch (key) {
      case 'payee':
        setPayee(uniformPayee ?? '');
        break;
      case 'category':
        setCategory(uniformCategory !== null ? some(uniformCategory) : none);
        break;
      case 'description':
        setDescription(uniformDescription ?? '');
        break;
      case 'pending':
        setPending(uniformPending ?? false);
        break;
      default:
        break;
    }
  };

  // Adding and removing the same tag (or field) would be order-dependent —
  // the newer intent wins and clears the older one.
  const handleAddChange = (tags: string[], fieldValues: Record<string, string>) => {
    setAddTags(tags);
    setSetFields(fieldValues);
    setRemoveTags((prev) => {
      const next = new Set(prev);
      tags.forEach((tag) => next.delete(tag));
      return next;
    });
    setRemoveFieldIds((prev) => {
      const next = new Set(prev);
      Object.keys(fieldValues).forEach((id) => next.delete(id));
      return next;
    });
  };
  const toggleRemoveTag = (tag: string) => {
    setRemoveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
        setAddTags((tags) => tags.filter((t) => t !== tag));
      }
      return next;
    });
  };
  const toggleRemoveField = (fieldId: string) => {
    setRemoveFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
        setSetFields((fields) => {
          const {[fieldId]: _removed, ...rest} = fields;
          return rest;
        });
      }
      return next;
    });
  };

  // Category goes to the payments; everything else to the whole selection.
  // Clearing a payee is a transfer-only op (payments require one), so with
  // payments selected it blocks Save instead.
  const paymentOps: TransactionPatchOp[] = [];
  const sharedOps: TransactionPatchOp[] = [];
  if (isActive('payee')) {
    sharedOps.push(
      payee.trim()
        ? {field: 'payee', op: PatchOpType.Set, value: payee.trim()}
        : {field: 'payee', op: PatchOpType.Unset},
    );
  }
  if (isActive('category')) {
    paymentOps.push(
      category
        .map((name): TransactionPatchOp => ({
          field: 'category',
          op: PatchOpType.Set,
          value: name,
        }))
        .getOrElse({field: 'category', op: PatchOpType.Unset}),
    );
  }
  if (isActive('description')) {
    sharedOps.push(
      description.trim()
        ? {field: 'description', op: PatchOpType.Set, value: description.trim()}
        : {field: 'description', op: PatchOpType.Unset},
    );
  }
  if (isActive('pending')) {
    sharedOps.push({field: 'pending', op: PatchOpType.Set, value: pending});
  }
  if (isActive('tagsAndFields')) {
    if (addTags.length > 0) {
      sharedOps.push({field: 'tags', op: PatchOpType.Add, value: addTags});
    }
    if (removeTags.size > 0) {
      sharedOps.push({field: 'tags', op: PatchOpType.Remove, value: [...removeTags]});
    }
    if (Object.keys(setFields).length > 0) {
      sharedOps.push({field: 'dimensionValues', op: PatchOpType.Add, value: setFields});
    }
    if (removeFieldIds.size > 0) {
      sharedOps.push({
        field: 'dimensionValues',
        op: PatchOpType.Remove,
        value: [...removeFieldIds],
      });
    }
  }
  // The rejections the server would make, caught before anything is written:
  // a payment losing its payee or category, or any transaction losing a
  // required field. With those ruled out, the two patch calls below take only
  // values the form already constrained, so a save can't land half-applied.
  // Only what will be sent counts: removals staged on a row since left
  // unchanged are dropped from the ops, so they don't gate Save either.
  const stagedFieldRemovals = isActive('tagsAndFields')
    ? removeFieldIds
    : new Set<string>();
  const removedRequiredFieldNames = (fieldsData?.allFields ?? [])
    .filter((f) => f.required && stagedFieldRemovals.has(f._id))
    .map((f) => f.name);
  const clearsRequired =
    (isActive('payee') && payee.trim() === '' && payments.length > 0) ||
    (isActive('category') && category.isEmpty) ||
    removedRequiredFieldNames.length > 0 ||
    // Unverifiable until the fields load.
    (stagedFieldRemovals.size > 0 && fieldsData === undefined);
  const hasChanges = paymentOps.length > 0 || sharedOps.length > 0;

  const [patchTransactions] = usePatchTransactionsMutation();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasChanges || clearsRequired || saving) return;
    setError(null);
    setSaving(true);
    // A patch can introduce a lookup value typed inline (a new category, tag,
    // or payee); the normalized entities don't join the cached root lists on
    // their own.
    const mergeLookupValues = (
      store: ApolloCache<object>,
      result: FetchResult<PatchTransactionsMutation>,
    ) => {
      for (const tx of result.data?.patchTransactions ?? []) {
        if (tx.category) addNewCategory(store, tx.category);
        addNewTags(store, tx.tags);
        if (tx.payee) addPayee(store, tx.payee);
      }
    };
    // patchTransactions validates every op before its one write, so ops on
    // the same targets ride one call and land or fail together. Only a
    // selection mixing payments and transfers needs a second call, for the
    // category the transfers can't take.
    const calls =
      transfers.length === 0
        ? [{ids: transactions.map((t) => t._id), operations: [...paymentOps, ...sharedOps]}]
        : [
            {ids: transactions.map((t) => t._id), operations: sharedOps},
            {ids: payments.map((t) => t._id), operations: paymentOps},
          ];
    const run = async () => {
      for (const call of calls) {
        if (call.operations.length === 0 || call.ids.length === 0) continue;
        await patchTransactions({
          variables: {transactionIds: call.ids, operations: call.operations},
          update: mergeLookupValues,
        });
      }
    };
    run()
      .then(() => {
        onRequestClose();
        onSaved?.();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to save changes.');
      })
      .finally(() => setSaving(false));
  };

  const tagsSummary =
    tagCounts.size === 0 && fieldCounts.size === 0
      ? 'None'
      : [
          tagCounts.size > 0
            ? `${tagCounts.size} tag${tagCounts.size === 1 ? '' : 's'}`
            : null,
          fieldCounts.size > 0
            ? `${fieldCounts.size} field${fieldCounts.size === 1 ? '' : 's'}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ');

  const kinds = [
    payments.length > 0
      ? `${payments.length} payment${payments.length === 1 ? '' : 's'}`
      : null,
    transferCount > 0
      ? `${transferCount} transfer${transferCount === 1 ? '' : 's'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      onRequestClose={onRequestClose}
      // The edit sheet's frame: a bottom sheet on a phone.
      className="top-auto bottom-0 h-auto max-h-[92dvh] rounded-b-none sm:rounded-b-lg"
      header={
        <div className="flex flex-col">
          <span>Edit {transactions.length} transactions</span>
          <span className="text-muted-foreground text-sm font-normal">{kinds}</span>
        </div>
      }
      footer={
        <>
          <Button variant="outline" disabled={saving} onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={!hasChanges || clearsRequired || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
        <PropertyRow
          label="Payee"
          summary={
            uniformPayee === null
              ? MIXED
              : uniformPayee || <span className="italic">Empty</span>
          }
          active={isActive('payee')}
          onToggle={toggleRow('payee')}
        >
          <PayeeInput value={payee} onChange={setPayee} />
        </PropertyRow>
        <PropertyRow
          label="Category"
          summary={
            payments.length === 0
              ? 'None'
              : uniformCategory === null
                ? payments.some((t) => t.category != null)
                  ? MIXED
                  : 'None'
                : uniformCategory
          }
          active={isActive('category')}
          disabledReason={categoryReason}
          onToggle={toggleRow('category')}
        >
          <CategoryPicker selectedCategory={category} onSelectCategory={setCategory} />
        </PropertyRow>
        <PropertyRow
          label="Note"
          summary={
            uniformDescription === null
              ? MIXED
              : uniformDescription || <span className="italic">Empty</span>
          }
          active={isActive('description')}
          onToggle={toggleRow('description')}
        >
          <TextInput value={description} onChange={setDescription} placeholder="Note" />
        </PropertyRow>
        <PropertyRow
          label="Tags & fields"
          summary={tagsSummary}
          active={isActive('tagsAndFields')}
          onToggle={toggleRow('tagsAndFields')}
        >
          <div className="flex flex-col gap-2">
            <TagsAndFieldsInput
              tags={addTags}
              fieldValues={setFields}
              onChange={handleAddChange}
            />
            {(tagCounts.size > 0 || fieldCounts.size > 0) && (
              <div className="flex flex-wrap gap-1">
                {[...tagCounts.entries()].map(([tag, count]) => (
                  <RemovalChip
                    key={`tag:${tag}`}
                    label={tag}
                    count={count}
                    armed={removeTags.has(tag)}
                    onToggle={() => toggleRemoveTag(tag)}
                  />
                ))}
                {[...fieldCounts.entries()].map(([fieldId, count]) => (
                  <RemovalChip
                    key={`field:${fieldId}`}
                    label={fieldName(fieldId)}
                    count={count}
                    armed={removeFieldIds.has(fieldId)}
                    onToggle={() => toggleRemoveField(fieldId)}
                  />
                ))}
              </div>
            )}
          </div>
        </PropertyRow>
        <PropertyRow
          label="Pending"
          summary={uniformPending === null ? MIXED : uniformPending ? 'Yes' : 'No'}
          active={isActive('pending')}
          onToggle={toggleRow('pending')}
        >
          <div className="pt-1.5">
            <PendingCheckbox value={pending} onChange={setPending} />
          </div>
        </PropertyRow>
        {stagedFieldRemovals.size > 0 && fieldsData === undefined && fieldsError && (
          // Which fields are required isn't known, so a removal can't be
          // checked, and Save waits on it.
          <p className="text-destructive flex items-center gap-2 text-sm">
            Couldn&apos;t load fields.
            <Button
              variant="outline"
              className="h-7 px-2.5 text-xs"
              onClick={() => void refetchFields()}
            >
              Retry
            </Button>
          </p>
        )}
        {removedRequiredFieldNames.length > 0 && (
          <p className="text-destructive text-sm">
            {removedRequiredFieldNames.join(', ')} can&apos;t be removed.
          </p>
        )}
        {error && <p className="text-destructive text-sm">{error}</p>}
      </form>
    </Modal>
  );
};
